import { useEffect, useState } from "react";
import { Loader2, ScrollText, Search } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { DISPLAY_TZ } from "@/lib/app-utils";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataPagination } from "@/components/ui/data-pagination";

const PAGE_SIZE = 50;

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  async function load(toPage = page) {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit-log", { params: { page: toPage, page_size: PAGE_SIZE, action: q || undefined } });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(r.data.page || toPage);
    }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, []);

  function search() { setPage(1); load(1); }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="audit-title">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Immutable record of admin actions. Search by action prefix (e.g., <code>integration.</code>).</p>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="audit-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Filter by action prefix…" className="pl-8 h-9" onKeyDown={(e)=>e.key==="Enter"&&search()} />
        </div>
        <button data-testid="audit-refresh" onClick={search} className="h-9 px-3 rounded-md border border-border text-sm hover:bg-muted">Refresh</button>
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground"><ScrollText className="w-6 h-6 mx-auto mb-2 opacity-40" />No events.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
                <th className="px-4 py-2">When</th><th className="px-4 py-2">Actor</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Action</th><th className="px-4 py-2">Target</th><th className="px-4 py-2">Meta</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`audit-row-${r.id}`}>
                    <td className="px-4 py-2 text-xs whitespace-nowrap">{new Date(r.ts).toLocaleString([], { timeZone: DISPLAY_TZ })}</td>
                    <td className="px-4 py-2 text-xs">{r.actor_email || r.actor_id}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className="text-[10px] uppercase">{(r.actor_role||"").replace(/_/g," ")}</Badge></td>
                    <td className="px-4 py-2"><code className="text-xs">{r.action}</code></td>
                    <td className="px-4 py-2 text-xs"><code>{JSON.stringify(r.target||{}).slice(0,80)}</code></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground"><code>{JSON.stringify(r.meta||{}).slice(0,80)}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DataPagination page={page} pages={pages} total={total} pageSize={PAGE_SIZE} onPage={(p) => load(p)} testId="audit-pagination" />
      </Card>
    </div>
  );
}
